import React, { useCallback, useState } from "react";
import { Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import DashboardReport from "components/reports/templates/DashboardReport";
import DrillDownDrawer from "components/reports/shared/DrillDownDrawer";
import ReportLineChart from "components/reports/charts/ReportLineChart";
import ReportBarChart from "components/reports/charts/ReportBarChart";
import ReportNestedPieChart from "components/reports/charts/ReportNestedPieChart";
import { getChartColors } from "theme";
import api from "services/api";

const formatCurrency = (value) =>
  `$${(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatPercent = (value) => `${value > 0 ? "+" : ""}${(value || 0).toFixed(2)}%`;

const toMonthLabel = (value) => {
  if (!value) return "-";

  if (typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) {
    const [year, month] = value.split("-");
    const date = new Date(Number(year), Number(month) - 1, 1);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    }
  }

  return value;
};

const createDrawerInitialState = () => ({
  open: false,
  title: "",
  subtitle: "",
  description: "",
  data: [],
  columns: [],
  summary: null,
});

const getGrowthBusinessTrendsData = async (filters) => {
  const [revenueSummaryRes, serviceVolumeRes, clientSlaRes] = await Promise.all([
    api.get("/reports/financial/revenue-summary", { params: filters }),
    api.get("/reports/service/volume", { params: filters }),
    api.get("/reports/client/sla", { params: filters }),
  ]);

  const revenueData = revenueSummaryRes.data?.data || {};
  const monthlyTrend = revenueData.monthlyTrend || {};

  const revenueMonths = monthlyTrend.months || [];
  const revenueRows = revenueMonths.map((month, index) => ({
    month,
    revenue: Number(monthlyTrend.revenue?.[index] || 0),
    servicesCount: Number(monthlyTrend.servicesCount?.[index] || 0),
    yoyGrowth: Number(monthlyTrend.yoyGrowth?.[index] || 0),
    cost: Number(monthlyTrend.costs?.[index] || 0),
    opex: Number(monthlyTrend.opex?.[index] || 0),
    translationRevenue: Number(monthlyTrend.translationRevenue?.[index] || 0),
    interpretationRevenue: Number(monthlyTrend.interpretationRevenue?.[index] || 0),
    otherServicesRevenue: Number(monthlyTrend.otherServicesRevenue?.[index] || 0),
  }));

  const serviceVolumeRows = (serviceVolumeRes.data?.data?.tableData || [])
    .map((row) => ({
      ...row,
      month: row.month,
      monthLabel: toMonthLabel(row.month),
      total: Number(row.total || 0),
      translation: Number(row.translation || 0),
      interpretation: Number(row.interpretation || 0),
      other: Number(row.other || 0),
    }))
    .sort((a, b) => (a.month || "").localeCompare(b.month || ""));

  const slaSummary = clientSlaRes.data?.data?.summary || {};

  const seasonalityRows = Object.entries(revenueData.seasonalityIndex || {}).map(
    ([quarter, index]) => ({
      quarter,
      index: Number(index || 0),
    }),
  );

  return {
    data: {
      summary: {
        periodRevenue: Number(revenueData.periodRevenue || 0),
        periodGrowth: Number(revenueData.periodGrowth || 0),
        avgMonthlyRevenue: Number(revenueData.avgMonthlyRevenue || 0),
        revenueVelocity: Number(revenueData.revenueVelocity || 0),
        totalServices: Number(revenueData.totalServices || 0),
        clientRetentionRate: Number(revenueData.clientRetentionRate || 0),
        linguistRetentionRate: Number(revenueData.linguistRetentionRate || 0),
        completionRate: Number(slaSummary.completionRate || 0),
        onTimeRate: Number(slaSummary.onTimeRate || 0),
        cancellationRate: Number(slaSummary.cancellationRate || 0),
      },
      revenueRows,
      serviceVolumeRows,
      seasonalityRows,
      topClients: revenueData.topClients || [],
      periodMetadata: revenueData.periodMetadata || {},
    },
  };
};

export const GrowthBusinessTrends = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const [drillDown, setDrillDown] = useState(createDrawerInitialState());

  const closeDrillDown = useCallback(() => {
    setDrillDown(createDrawerInitialState());
  }, []);

  const openDrillDown = useCallback((payload) => {
    setDrillDown({
      open: true,
      title: payload.title || "Details",
      subtitle: payload.subtitle || "",
      description: payload.description || "",
      data: payload.data || [],
      columns: payload.columns || [],
      summary: payload.summary || null,
    });
  }, []);

  const dataFetchFn = useCallback(async (activeFilters) => {
    return getGrowthBusinessTrendsData(activeFilters);
  }, []);

  const metricsConfig = useCallback(
    (data) => {
      const { summary } = data;

      return [
        {
          title: "Period Revenue",
          value: formatCurrency(summary.periodRevenue),
          subtitle: `${summary.totalServices.toLocaleString()} services in selected period`,
          tooltipTitle: "Period Revenue",
          tooltipContent: `Period Revenue is the total billed amount for all filtered services in the selected date window.

Formula:
Period Revenue = Sum of Account Charge across filtered services

How to interpret:
• Rising revenue with stable completion and on-time rates suggests healthy, scalable growth.
• Rising revenue with falling completion or on-time rates may indicate growth that is operationally fragile.
• Flat revenue with rising cancellations usually signals hidden churn risk.

Immediate actions:
1. Compare this period against previous comparable period.
2. Verify growth quality using completion and on-time metrics.
3. Segment growth by service category to identify what's actually driving performance.`,
          onClick: () =>
            openDrillDown({
              title: "Revenue Trend Detail",
              subtitle: `${data.revenueRows.length} months`,
              description:
                "Month-level revenue progression with service counts and YoY growth context.",
              data: data.revenueRows,
              columns: [
                { field: "month", headerName: "Month", width: 130 },
                {
                  field: "revenue",
                  headerName: "Revenue",
                  width: 140,
                  renderCell: (row) => formatCurrency(row.revenue),
                },
                {
                  field: "servicesCount",
                  headerName: "Services",
                  width: 100,
                },
                {
                  field: "yoyGrowth",
                  headerName: "YoY Growth",
                  width: 110,
                  renderCell: (row) => formatPercent(row.yoyGrowth),
                },
              ],
            }),
        },
        {
          title: "Period Growth",
          value: formatPercent(summary.periodGrowth),
          subtitle: "Compared with previous comparable period",
          tooltipTitle: "Period Growth",
          tooltipContent: `Period Growth measures relative revenue change between this period and the prior comparable period.

Formula:
Period Growth (%) = ((Current Period Revenue - Previous Period Revenue) / Previous Period Revenue) × 100

How to read this correctly:
• Positive growth is only healthy if quality metrics hold (completion, on-time, cancellation).
• Very high growth with lower margins or weaker reliability may be unsustainable.
• Slight negative growth can be acceptable if profitability and reliability improve.

Diagnostic checks:
1. Is growth concentrated in one client or category?
2. Is growth matched by capacity improvements?
3. Did cancellations or late deliveries increase alongside growth?`,
          color: summary.periodGrowth >= 0 ? "success.main" : "error.main",
          onClick: () =>
            openDrillDown({
              title: "Growth Diagnostics",
              subtitle: `${formatPercent(summary.periodGrowth)} period growth`,
              description:
                "Review monthly growth path, concentration by top clients, and consistency of growth quality.",
              data: (data.topClients || []).slice(0, 15).map((client) => ({
                ...client,
                revenue: Number(client.revenue || 0),
                percentOfTotal: Number(client.percentOfTotal || 0),
              })),
              columns: [
                { field: "name", headerName: "Client", width: 250 },
                {
                  field: "revenue",
                  headerName: "Revenue",
                  width: 140,
                  renderCell: (row) => formatCurrency(row.revenue),
                },
                {
                  field: "servicesCount",
                  headerName: "Services",
                  width: 100,
                },
                {
                  field: "percentOfTotal",
                  headerName: "% of Total",
                  width: 110,
                  renderCell: (row) => `${(row.percentOfTotal || 0).toFixed(1)}%`,
                },
              ],
            }),
        },
        {
          title: "Revenue Velocity",
          value: formatPercent(summary.revenueVelocity),
          subtitle: "Average month-over-month growth pace",
          tooltipTitle: "Revenue Velocity",
          tooltipContent: `Revenue Velocity estimates average month-over-month growth momentum.

Formula (simplified):
Revenue Velocity = Average of monthly growth rates

Why this matters:
• Revenue level tells you where you are.
• Revenue velocity tells you how fast trajectory is changing.
• Falling velocity often appears before visible revenue stagnation.

Action guidance:
1. If velocity declines for 2+ months, review pipeline quality and service mix.
2. If velocity rises but completion/on-time declines, growth may be overloading operations.
3. Track velocity with seasonality to avoid false alarms from normal cycles.`,
          onClick: () =>
            openDrillDown({
              title: "Velocity & Seasonality Detail",
              subtitle: `${formatPercent(summary.revenueVelocity)} average monthly momentum`,
              description:
                "Quarterly seasonality index and monthly trend context to distinguish true acceleration from seasonal patterns.",
              data: data.seasonalityRows,
              columns: [
                { field: "quarter", headerName: "Quarter", width: 120 },
                {
                  field: "index",
                  headerName: "Seasonality Index",
                  width: 150,
                  renderCell: (row) => `${(row.index || 0).toFixed(1)}`,
                },
              ],
            }),
        },
        {
          title: "Avg Monthly Revenue",
          value: formatCurrency(summary.avgMonthlyRevenue),
          subtitle: `${formatPercent(summary.onTimeRate)} on-time • ${formatPercent(summary.completionRate)} completion`,
          tooltipTitle: "Average Monthly Revenue",
          tooltipContent: `Average Monthly Revenue smooths period volatility and gives a planning baseline.

Formula:
Average Monthly Revenue = Period Revenue / Number of months in period

Operational context:
• Use this for budgeting, staffing plans, and target setting.
• Pair with on-time and completion rates to distinguish healthy average from stressed average.

Quality checkpoints:
• If avg revenue rises while completion drops, investigate bottlenecks.
• If avg revenue is stable but cancellation rises, growth quality is deteriorating.
• Track monthly variance around the average to plan capacity buffers.`,
          onClick: () =>
            openDrillDown({
              title: "Service Volume vs Revenue",
              subtitle: `${data.serviceVolumeRows.length} monthly volume points`,
              description:
                "Use this to compare demand volume movement against revenue trend and identify productivity or pricing shifts.",
              data: data.serviceVolumeRows,
              columns: [
                { field: "monthLabel", headerName: "Month", width: 130 },
                { field: "total", headerName: "Total Services", width: 120 },
                { field: "translation", headerName: "Translation", width: 120 },
                { field: "interpretation", headerName: "Interpretation", width: 130 },
                { field: "other", headerName: "Other", width: 100 },
              ],
            }),
        },
      ];
    },
    [openDrillDown],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <ReportLineChart
            title="Revenue & Service Trend"
            tooltipTitle="Revenue & Service Trend"
            tooltipContent={`This chart combines monthly revenue and service volume so you can see whether growth is driven by higher volume, higher pricing, or both.

How to use:
• Revenue up + services flat = pricing/mix effect.
• Services up + revenue flat = yield pressure.
• Both up = broad growth.

Use this with completion and on-time KPIs to validate growth quality.`}
            xAxisData={data.revenueRows.map((row) => row.month)}
            series={[
              {
                label: "Revenue",
                data: data.revenueRows.map((row) => row.revenue),
                color: chartColors.primary,
                valueFormatter: (value) => formatCurrency(value),
                yAxisId: "leftAxis",
              },
              {
                label: "Services",
                data: data.revenueRows.map((row) => row.servicesCount),
                color: chartColors.info,
                valueFormatter: (value) => `${(value || 0).toLocaleString()} services`,
                yAxisId: "rightAxis",
              },
            ]}
            yAxisConfigs={[
              {
                id: "leftAxis",
                label: "Revenue",
                min: 0,
              },
              {
                id: "rightAxis",
                label: "Services",
                min: 0,
              },
            ]}
            leftAxis="leftAxis"
            rightAxis="rightAxis"
            height={350}
            chartMargin={{ top: 20, right: 80, bottom: 90, left: 80 }}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: 0,
              },
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportLineChart
            title="Monthly YoY Growth"
            tooltipTitle="Monthly YoY Growth"
            tooltipContent={`Shows year-over-year growth by month.

Use this to detect:
• Consistent acceleration/deceleration patterns.
• Seasonal weak points needing proactive action.
• Growth concentration in only a few months.`}
            xAxisData={data.revenueRows.map((row) => row.month)}
            series={[
              {
                label: "YoY Growth %",
                data: data.revenueRows.map((row) => row.yoyGrowth),
                color: chartColors.error,
                valueFormatter: (value) => formatPercent(value),
                curve: "monotoneX",
                showMark: true,
              },
            ]}
            yAxisConfigs={[
              {
                label: "YoY %",
              },
            ]}
            chartMargin={{ top: 10, right: 20, bottom: 80, left: 80 }}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: 0,
              },
            }}
            height={320}
            onMarkClick={(dataIndex) => {
              const item = data.revenueRows[dataIndex];
              if (!item) return;
              openDrillDown({
                title: `Monthly Growth Detail: ${item.month}`,
                subtitle: `${formatPercent(item.yoyGrowth)} YoY`,
                description:
                  "Single-month growth snapshot including revenue, services, and category-level revenue split.",
                data: [item],
                columns: [
                  { field: "month", headerName: "Month", width: 120 },
                  {
                    field: "revenue",
                    headerName: "Revenue",
                    width: 130,
                    renderCell: (row) => formatCurrency(row.revenue),
                  },
                  {
                    field: "servicesCount",
                    headerName: "Services",
                    width: 100,
                  },
                  {
                    field: "yoyGrowth",
                    headerName: "YoY Growth",
                    width: 110,
                    renderCell: (row) => formatPercent(row.yoyGrowth),
                  },
                  {
                    field: "translationRevenue",
                    headerName: "Translation Rev",
                    width: 140,
                    renderCell: (row) => formatCurrency(row.translationRevenue),
                  },
                  {
                    field: "interpretationRevenue",
                    headerName: "Interpretation Rev",
                    width: 150,
                    renderCell: (row) => formatCurrency(row.interpretationRevenue),
                  },
                ],
              });
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Seasonality Index by Quarter"
            tooltipTitle="Seasonality Index by Quarter"
            tooltipContent={`Seasonality Index compares quarter revenue against average quarter revenue (index 100 = baseline).

Interpretation:
• >100 means above-average seasonal strength.
• <100 means below-average quarter.
• Use this to schedule staffing, sales campaigns, and cost controls before expected shifts.`}
            data={data.seasonalityRows}
            xAxisKey="quarter"
            series={[
              {
                dataKey: "index",
                label: "Index",
                color: chartColors.warning,
                valueFormatter: (value) => `${(value || 0).toFixed(1)}`,
              },
            ]}
            yAxisLabel="Index"
            valueFormatter={(value) => `${(value || 0).toFixed(1)}`}
            yAxisScaleType="linear"
            height={320}
          />
        </Grid>
      </Grid>
    ),
    [chartColors.error, chartColors.info, chartColors.primary, chartColors.warning, openDrillDown],
  );

  return (
    <>
      <DashboardReport
        title="Growth & Business Trends"
        description="Long-range growth analysis combining revenue trajectory, demand movement, and seasonal behavior to support forward planning."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={
          typeof drillDown.description === "string" ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
              {drillDown.description}
            </Typography>
          ) : (
            drillDown.description
          )
        }
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

// ─── Service Mix & Efficiency ─────────────────────────────────────────────────

const SERVICE_MIX_COLUMNS = [
  { field: "serviceType", headerName: "Service Type", width: 180 },
  { field: "category", headerName: "Category", width: 130 },
  { field: "servicesCount", headerName: "# Services", width: 110 },
  {
    field: "revenue",
    headerName: "Revenue",
    width: 130,
    renderCell: (row) => formatCurrency(row.revenue),
  },
  {
    field: "cost",
    headerName: "Cost",
    width: 120,
    renderCell: (row) => formatCurrency(row.cost),
  },
  {
    field: "profit",
    headerName: "Profit",
    width: 120,
    renderCell: (row) => formatCurrency(row.profit),
  },
  {
    field: "profitMargin",
    headerName: "Margin %",
    width: 110,
    renderCell: (row) => `${(row.profitMargin || 0).toFixed(1)}%`,
  },
  {
    field: "avgRevenuePerService",
    headerName: "Avg Rev/Service",
    width: 140,
    renderCell: (row) => formatCurrency(row.avgRevenuePerService),
  },
];

const VOLUME_BY_MONTH_COLUMNS = [
  { field: "month", headerName: "Month", width: 120 },
  { field: "total", headerName: "Total", width: 90 },
  { field: "translation", headerName: "Translation", width: 120 },
  { field: "interpretation", headerName: "Interpretation", width: 140 },
  { field: "other", headerName: "Other", width: 90 },
];

const getServiceMixData = async (filters) => {
  const [revByServiceRes, profitabilityRes, volumeRes, turnaroundRes] = await Promise.all([
    api.get("/reports/financial/revenue-by-service", { params: filters }),
    api.get("/reports/financial/service-profitability", { params: filters }),
    api.get("/reports/service/volume", { params: filters }),
    api.get("/reports/service/turnaround", { params: filters }),
  ]);

  const serviceRevenueRows = revByServiceRes.data?.data?.tableData || [];
  const profitRows = profitabilityRes.data?.data?.tableData || [];
  const volumeTableData = volumeRes.data?.data?.tableData || [];
  const totalServices = volumeRes.data?.data?.totalServices || 0;
  const avgTurnaround = turnaroundRes.data?.data?.avgTurnaround || 0;
  const onTimeRate = turnaroundRes.data?.data?.onTimeRate || 0;

  const totalRevenue = serviceRevenueRows.reduce((s, r) => s + r.revenue, 0);
  const avgRevenuePerService = totalServices > 0 ? totalRevenue / totalServices : 0;

  const volumeRows = volumeTableData.map((row) => ({
    ...row,
    month: toMonthLabel(row.month),
  }));

  return {
    data: {
      metrics: { totalServices, avgRevenuePerService, avgTurnaround, onTimeRate },
      serviceRevenueRows,
      profitRows,
      volumeRows,
    },
  };
};

const ServiceMixEfficiency = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [drillDown, setDrillDown] = useState(createDrawerInitialState());

  const openDrillDown = useCallback((config) => setDrillDown({ open: true, ...config }), []);
  const closeDrillDown = useCallback(
    () => setDrillDown((prev) => ({ ...prev, open: false })),
    [],
  );

  const dataFetchFn = useCallback((f) => getServiceMixData(f), []);

  const metricsConfig = useCallback(
    (data) => [
      {
        label: "Total Services",
        value: (data.metrics.totalServices || 0).toLocaleString(),
        tooltipContent: "Total number of services completed in the selected period.",
        onClick: () =>
          openDrillDown({
            title: "Service Volume by Month",
            subtitle: "",
            description: "Breakdown of service volume by category for each month.",
            data: data.volumeRows,
            columns: VOLUME_BY_MONTH_COLUMNS,
          }),
      },
      {
        label: "Avg Revenue / Service",
        value: formatCurrency(data.metrics.avgRevenuePerService),
        tooltipContent:
          "Average revenue earned per service \u2014 a yield indicator. Rising value means better pricing or mix shift toward higher-value services.",
        onClick: () =>
          openDrillDown({
            title: "Revenue by Service Type",
            subtitle: "",
            description: "Revenue, cost and margin breakdown for each service type.",
            data: data.serviceRevenueRows,
            columns: SERVICE_MIX_COLUMNS,
          }),
      },
      {
        label: "Avg Turnaround",
        value: `${(data.metrics.avgTurnaround || 0).toFixed(1)} days`,
        tooltipContent:
          "Average calendar days from service request to delivery. Lower is better for client satisfaction.",
        onClick: () =>
          openDrillDown({
            title: "Profit Margin by Service Type",
            subtitle: "",
            description: "Profitability breakdown ordered by margin percentage.",
            data: data.profitRows,
            columns: [
              { field: "serviceType", headerName: "Service Type", width: 180 },
              { field: "servicesCount", headerName: "# Services", width: 110 },
              {
                field: "revenue",
                headerName: "Revenue",
                width: 130,
                renderCell: (row) => formatCurrency(row.revenue),
              },
              {
                field: "profit",
                headerName: "Profit",
                width: 120,
                renderCell: (row) => formatCurrency(row.profit),
              },
              {
                field: "profitMargin",
                headerName: "Margin %",
                width: 110,
                renderCell: (row) => `${(row.profitMargin || 0).toFixed(1)}%`,
              },
            ],
          }),
      },
      {
        label: "On-Time Rate",
        value: `${(data.metrics.onTimeRate || 0).toFixed(1)}%`,
        tooltipContent:
          "Percentage of translation services delivered on or before the deadline. A key SLA efficiency metric.",
        onClick: () =>
          openDrillDown({
            title: "Service Mix Overview",
            subtitle: "",
            description: "Full revenue and margin detail across all service types.",
            data: data.serviceRevenueRows,
            columns: SERVICE_MIX_COLUMNS,
          }),
      },
    ],
    [openDrillDown],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <ReportNestedPieChart
            title="Revenue Mix by Service Type"
            tooltipTitle="Revenue Mix by Service Type"
            tooltipContent={`Inner ring = category totals (Translation / Interpretation / Other).\nOuter ring = individual service types within each category, shaded by revenue rank.\n\nClick an inner slice to see all types in that category.\nClick an outer slice for full detail on that service type.`}
            serviceRows={data.serviceRevenueRows}
            valueFormatter={formatCurrency}
            height={320}
            onCategoryClick={(cat) =>
              openDrillDown({
                title: `Category: ${cat.category}`,
                subtitle: formatCurrency(cat.revenue),
                description: `All service types under the ${cat.category} category, ordered by revenue.`,
                data: cat.rows.slice().sort((a, b) => b.revenue - a.revenue),
                columns: SERVICE_MIX_COLUMNS,
              })
            }
            onTypeClick={(row) =>
              openDrillDown({
                title: `Service Detail: ${row.serviceType}`,
                subtitle: `${formatCurrency(row.revenue)} \u00b7 ${(row.profitMargin || 0).toFixed(1)}% margin`,
                description: "Revenue, cost, profit, and efficiency metrics for this service type.",
                data: [row],
                columns: SERVICE_MIX_COLUMNS,
              })
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <ReportBarChart
            title="Profit Margin % by Service Type"
            tooltipTitle="Profit Margin % by Service Type"
            tooltipContent={`Ranks service types by their profit margin percentage.\n\nHigh margin + low volume = pricing opportunity.\nLow margin + high volume = cost efficiency target.\nCombine with revenue mix to find highest-impact levers.`}
            data={data.profitRows}
            xAxisKey="serviceType"
            series={[
              {
                dataKey: "profitMargin",
                label: "Margin %",
                color: chartColors.primary,
                valueFormatter: (v) => `${(v || 0).toFixed(1)}%`,
              },
            ]}
            yAxisLabel="Margin %"
            yAxisScaleType="linear"
            height={320}
            onBarClick={(item) =>
              openDrillDown({
                title: `Margin Detail: ${item.serviceType}`,
                subtitle: `${(item.profitMargin || 0).toFixed(1)}% margin`,
                description: "Full profitability breakdown for this service type.",
                data: [item],
                columns: [
                  { field: "serviceType", headerName: "Service Type", width: 180 },
                  { field: "servicesCount", headerName: "# Services", width: 110 },
                  {
                    field: "revenue",
                    headerName: "Revenue",
                    width: 130,
                    renderCell: (row) => formatCurrency(row.revenue),
                  },
                  {
                    field: "profit",
                    headerName: "Profit",
                    width: 120,
                    renderCell: (row) => formatCurrency(row.profit),
                  },
                  {
                    field: "profitMargin",
                    headerName: "Margin %",
                    width: 110,
                    renderCell: (row) => `${(row.profitMargin || 0).toFixed(1)}%`,
                  },
                  {
                    field: "avgProfitPerService",
                    headerName: "Avg Profit/Service",
                    width: 150,
                    renderCell: (row) => formatCurrency(row.avgProfitPerService),
                  },
                ],
              })
            }
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ReportBarChart
            title="Monthly Volume by Category"
            tooltipTitle="Monthly Volume by Category"
            tooltipContent={`Stacked view of monthly service counts split by category (Translation, Interpretation, Other).\n\nUse this to:\n\u2022 Track mix shift over time.\n\u2022 Spot months where one category surges.\n\u2022 Plan linguist capacity by service type.`}
            data={data.volumeRows}
            xAxisKey="month"
            series={[
              {
                dataKey: "translation",
                label: "Translation",
                color: chartColors.primary,
                valueFormatter: (v) => `${(v || 0).toLocaleString()} services`,
              },
              {
                dataKey: "interpretation",
                label: "Interpretation",
                color: chartColors.info,
                valueFormatter: (v) => `${(v || 0).toLocaleString()} services`,
              },
              {
                dataKey: "other",
                label: "Other",
                color: chartColors.warning,
                valueFormatter: (v) => `${(v || 0).toLocaleString()} services`,
              },
            ]}
            yAxisLabel="Services"
            yAxisScaleType="linear"
            height={300}
            chartMargin={{ top: 20, right: 20, bottom: 80, left: 80 }}
            legendSlotProps={{
              direction: "row",
              position: { vertical: "bottom", horizontal: "middle" },
              padding: 0,
            }}
            onBarClick={(item) =>
              openDrillDown({
                title: `Volume Detail: ${item.month}`,
                subtitle: `${item.total} total services`,
                description: "Service count breakdown by category for this month.",
                data: [item],
                columns: VOLUME_BY_MONTH_COLUMNS,
              })
            }
          />
        </Grid>
      </Grid>
    ),
    [chartColors.info, chartColors.primary, chartColors.warning, openDrillDown],
  );

  return (
    <>
      <DashboardReport
        title="Service Mix & Efficiency"
        description="Combines service mix optimization with operational efficiency metrics to improve margin-adjusted throughput."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={
          typeof drillDown.description === "string" ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
              {drillDown.description}
            </Typography>
          ) : (
            drillDown.description
          )
        }
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

const getPricingRateIntelligenceData = async (filters) => {
  const [profitMarginRes, revenueByServiceRes, serviceProfitabilityRes] = await Promise.all([
    api.get("/reports/financial/profit-margin", { params: filters }),
    api.get("/reports/financial/revenue-by-service", { params: filters }),
    api.get("/reports/financial/service-profitability", { params: filters }),
  ]);

  const profitData = profitMarginRes.data?.data || {};
  const serviceRateRows = (revenueByServiceRes.data?.data?.tableData || []).map((row) => ({
    ...row,
    revenue: Number(row.revenue || 0),
    cost: Number(row.cost || 0),
    profit: Number(row.profit || 0),
    servicesCount: Number(row.servicesCount || 0),
    profitMargin: Number(row.profitMargin || 0),
    avgRevenuePerService: Number(row.avgRevenuePerService || 0),
  }));

  const profitabilityRows = (serviceProfitabilityRes.data?.data?.tableData || []).map((row) => ({
    ...row,
    revenue: Number(row.revenue || 0),
    cost: Number(row.cost || 0),
    profit: Number(row.profit || 0),
    servicesCount: Number(row.servicesCount || 0),
    profitMargin: Number(row.profitMargin || 0),
    avgProfitPerService: Number(row.avgProfitPerService || 0),
  }));

  const monthlyData = profitData.monthlyData || {};
  const monthlyRows = (monthlyData.months || []).map((month, index) => {
    const revenue = Number(monthlyData.revenue?.[index] || 0);
    const costs = Number(monthlyData.costs?.[index] || 0);
    const profit = Number(monthlyData.profit?.[index] || 0);
    const margin = Number(
      monthlyData.margin?.[index] || (revenue > 0 ? (profit / revenue) * 100 : 0),
    );
    return {
      month,
      monthLabel: toMonthLabel(month),
      revenue,
      costs,
      profit,
      margin,
      servicesCount: Number(monthlyData.servicesCount?.[index] || 0),
    };
  });

  return {
    data: {
      summary: {
        totalRevenue: Number(profitData.totalRevenue || 0),
        totalCosts: Number(profitData.totalCosts || 0),
        totalProfit: Number(profitData.totalProfit || 0),
        avgProfitMargin: Number(profitData.avgProfitMargin || 0),
        profitableServices: Number(profitData.profitabilityMix?.profitableServices || 0),
        lossMakingServices: Number(profitData.profitabilityMix?.lossMakingServices || 0),
        breakEvenServices: Number(profitData.profitabilityMix?.breakEvenServices || 0),
        profitableRate: Number(profitData.profitabilityMix?.profitableRate || 0),
        lossRate: Number(profitData.profitabilityMix?.lossRate || 0),
        yoyRevenueGrowth: Number(profitData.yoyRevenueGrowth || 0),
        yoyGrossProfitGrowth: Number(profitData.yoyGrossProfitGrowth || 0),
      },
      serviceRateRows,
      topRateRows: [...serviceRateRows]
        .sort((a, b) => b.avgRevenuePerService - a.avgRevenuePerService)
        .slice(0, 10),
      profitabilityRows,
      topMarginRows: [...profitabilityRows]
        .sort((a, b) => b.profitMargin - a.profitMargin)
        .slice(0, 10),
      marginBandRows: (profitData.marginBandData || []).map((row) => ({
        ...row,
        servicesCount: Number(row.servicesCount || 0),
        revenue: Number(row.revenue || 0),
        profit: Number(row.profit || 0),
        avgMargin: Number(row.avgMargin || 0),
      })),
      monthlyRows,
      topClientsByMargin: (profitData.topClientsByMargin || []).map((row) => ({
        ...row,
        revenue: Number(row.revenue || 0),
        costs: Number(row.costs || 0),
        profit: Number(row.profit || 0),
        profitMargin: Number(row.profitMargin || 0),
      })),
      bottomClientsByMargin: (profitData.bottomClientsByMargin || []).map((row) => ({
        ...row,
        revenue: Number(row.revenue || 0),
        costs: Number(row.costs || 0),
        profit: Number(row.profit || 0),
        profitMargin: Number(row.profitMargin || 0),
      })),
    },
  };
};

export const PricingRateIntelligence = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [drillDown, setDrillDown] = useState(createDrawerInitialState());

  const openDrillDown = useCallback((config) => setDrillDown({ open: true, ...config }), []);
  const closeDrillDown = useCallback(
    () => setDrillDown((prev) => ({ ...prev, open: false })),
    [],
  );

  const dataFetchFn = useCallback((activeFilters) => getPricingRateIntelligenceData(activeFilters), []);

  const metricsConfig = useCallback(
    (data) => [
      {
        title: "Avg Profit Margin",
        value: `${(data.summary.avgProfitMargin || 0).toFixed(2)}%`,
        subtitle: `${formatCurrency(data.summary.totalProfit)} total profit`,
        color: data.summary.avgProfitMargin >= 20 ? "success.main" : "warning.main",
        tooltipTitle: "Average Profit Margin",
        tooltipContent: `Average Profit Margin shows overall pricing quality after direct delivery costs.

      Formula:
      Margin % = (Profit / Revenue) x 100

      How to interpret:
      • Rising margin with stable volume usually means better pricing discipline.
      • Falling margin with flat rates usually means costs are rising faster than price.
      • Healthy target depends on service mix, but persistent decline needs repricing review.`,
        onClick: () =>
          openDrillDown({
            title: "Margin Bands",
            subtitle: "Distribution of services by profitability band",
            description: "Each band shows service count, revenue and average margin.",
            data: data.marginBandRows,
            columns: [
              { field: "band", headerName: "Band", width: 170 },
              { field: "servicesCount", headerName: "Services", width: 100 },
              {
                field: "revenue",
                headerName: "Revenue",
                width: 130,
                renderCell: (row) => formatCurrency(row.revenue),
              },
              {
                field: "profit",
                headerName: "Profit",
                width: 130,
                renderCell: (row) => formatCurrency(row.profit),
              },
              {
                field: "avgMargin",
                headerName: "Avg Margin",
                width: 110,
                renderCell: (row) => `${(row.avgMargin || 0).toFixed(2)}%`,
              },
            ],
          }),
      },
      {
        title: "Profitable Service Rate",
        value: `${(data.summary.profitableRate || 0).toFixed(2)}%`,
        subtitle: `${(data.summary.lossRate || 0).toFixed(2)}% loss-making`,
        color: data.summary.profitableRate >= 75 ? "success.main" : "warning.main",
        tooltipTitle: "Profitable Service Rate",
        tooltipContent: `Profitable Service Rate tracks the share of jobs with positive unit economics.

      Formula:
      Profitable Rate % = Profitable Services / Total Services x 100

      How to use:
      • Low rate indicates underpriced work or cost leakage.
      • Compare with Avg Profit Margin to see whether losses are isolated or systemic.
      • Rising profitable rate is an early signal that rate-card changes are working.`,
        onClick: () =>
          openDrillDown({
            title: "Profitability Mix",
            subtitle: "Service-level economic outcome",
            description: "Breakdown of profitable, loss-making, and break-even services.",
            data: [
              {
                profitableServices: data.summary.profitableServices,
                lossMakingServices: data.summary.lossMakingServices,
                breakEvenServices: data.summary.breakEvenServices,
                profitableRate: data.summary.profitableRate,
                lossRate: data.summary.lossRate,
              },
            ],
            columns: [
              { field: "profitableServices", headerName: "Profitable", width: 110 },
              { field: "lossMakingServices", headerName: "Loss-Making", width: 120 },
              { field: "breakEvenServices", headerName: "Break-Even", width: 110 },
              {
                field: "profitableRate",
                headerName: "Profitable %",
                width: 110,
                renderCell: (row) => `${(row.profitableRate || 0).toFixed(2)}%`,
              },
              {
                field: "lossRate",
                headerName: "Loss %",
                width: 90,
                renderCell: (row) => `${(row.lossRate || 0).toFixed(2)}%`,
              },
            ],
          }),
      },
      {
        title: "YoY Gross Profit Growth",
        value: formatPercent(data.summary.yoyGrossProfitGrowth || 0),
        subtitle: `${formatPercent(data.summary.yoyRevenueGrowth || 0)} YoY revenue growth`,
        color: data.summary.yoyGrossProfitGrowth >= 0 ? "success.main" : "error.main",
        tooltipTitle: "YoY Gross Profit Growth",
        tooltipContent: `YoY Gross Profit Growth compares gross profit to the same period last year.

      Formula:
      YoY Growth % = (Current Gross Profit - Last Year Gross Profit) / |Last Year Gross Profit| x 100

      Interpretation:
      • Positive growth with stable margin = pricing and cost control are improving.
      • Positive revenue growth but weak profit growth = likely margin compression.
      • Negative growth should trigger client-level and service-level rate diagnostics.`,
        onClick: () => {
          const clientRows = [
            ...(data.topClientsByMargin || []).map((row) => ({ ...row, segment: "Top Margin" })),
            ...(data.bottomClientsByMargin || []).map((row) => ({ ...row, segment: "Bottom Margin" })),
          ];
          openDrillDown({
            title: "Client Margin Ranking",
            subtitle: "Top and bottom margin clients",
            description:
              "Review margin spread by client to identify repricing opportunities and account-level leakage.",
            data: clientRows,
            columns: [
              { field: "segment", headerName: "Segment", width: 120 },
              { field: "clientName", headerName: "Client", width: 180 },
              { field: "servicesCount", headerName: "Services", width: 90 },
              {
                field: "revenue",
                headerName: "Revenue",
                width: 130,
                renderCell: (row) => formatCurrency(row.revenue),
              },
              {
                field: "profit",
                headerName: "Profit",
                width: 120,
                renderCell: (row) => formatCurrency(row.profit),
              },
              {
                field: "profitMargin",
                headerName: "Margin %",
                width: 100,
                renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
              },
            ],
          });
        },
      },
      {
        title: "Total Revenue vs Cost",
        value: `${formatCurrency(data.summary.totalRevenue)} / ${formatCurrency(data.summary.totalCosts)}`,
        subtitle: "Revenue and direct cost baseline",
        tooltipTitle: "Revenue vs Cost",
        tooltipContent: `Revenue vs Cost is the fastest pricing-health check.

      What to watch:
      • If costs grow faster than revenue, margin compresses even when top-line rises.
      • If revenue and cost both rise but spread widens, pricing quality is improving.
      • If spread narrows, investigate rate concessions, overtime, and vendor-cost drift.`,
        onClick: () =>
          openDrillDown({
            title: "Service-Level Rate Sheet",
            subtitle: "Revenue, cost and margin by service type",
            description: "Use this view to identify rate outliers and services with weak unit economics.",
            data: data.serviceRateRows,
            columns: [
              { field: "serviceType", headerName: "Service Type", width: 180 },
              { field: "category", headerName: "Category", width: 120 },
              { field: "servicesCount", headerName: "Services", width: 90 },
              {
                field: "avgRevenuePerService",
                headerName: "Avg Rev/Service",
                width: 140,
                renderCell: (row) => formatCurrency(row.avgRevenuePerService),
              },
              {
                field: "profitMargin",
                headerName: "Margin %",
                width: 100,
                renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
              },
              {
                field: "profit",
                headerName: "Profit",
                width: 120,
                renderCell: (row) => formatCurrency(row.profit),
              },
            ],
          }),
      },
    ],
    [openDrillDown],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Avg Revenue per Service by Type"
            tooltipTitle="Avg Revenue per Service by Type"
            tooltipContent={`Compares average realized rate by service type.

Use this to detect:
• High-value services that deserve commercial focus.
• Underpriced services with strong demand.
• Rate spread between similar service types.

Formula:
Avg Rev/Service = Service Revenue / Service Count`}
            data={data.topRateRows}
            xAxisKey="serviceType"
            series={[
              {
                dataKey: "avgRevenuePerService",
                label: "Avg Rev/Service",
                color: chartColors.series[0],
                valueFormatter: (value) => formatCurrency(value),
              },
            ]}
            yAxisLabel="Avg Rev/Service"
            yAxisScaleType="linear"
            chartMargin={{ top: 70, right: 20, bottom: 90, left: 90 }}
            legendSlotProps={{
              direction: "row",
              position: { vertical: "top", horizontal: "middle" },
              padding: 0,
            }}
            height={320}
            onBarClick={(item) =>
              openDrillDown({
                title: `Rate Detail: ${item.serviceType}`,
                subtitle: `${formatCurrency(item.avgRevenuePerService)} avg revenue per service`,
                description: "Detailed pricing and margin profile for this service type.",
                data: [item],
                columns: [
                  { field: "serviceType", headerName: "Service Type", width: 180 },
                  { field: "category", headerName: "Category", width: 120 },
                  { field: "servicesCount", headerName: "Services", width: 90 },
                  {
                    field: "avgRevenuePerService",
                    headerName: "Avg Rev/Service",
                    width: 140,
                    renderCell: (row) => formatCurrency(row.avgRevenuePerService),
                  },
                  {
                    field: "profitMargin",
                    headerName: "Margin %",
                    width: 100,
                    renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
                  },
                  {
                    field: "profit",
                    headerName: "Profit",
                    width: 120,
                    renderCell: (row) => formatCurrency(row.profit),
                  },
                ],
              })
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Profit Margin by Service Type"
            tooltipTitle="Profit Margin by Service Type"
            tooltipContent={`Ranks service types by margin quality.

Interpretation:
• High margin + low volume = expansion candidate.
• Low/negative margin + high volume = repricing or cost-control target.

Decision cue:
Prioritize high-volume, low-margin types first because they create the largest profit leakage.`}
            data={data.topMarginRows}
            xAxisKey="serviceType"
            series={[
              {
                dataKey: "profitMargin",
                label: "Margin %",
                color: chartColors.series[2],
                valueFormatter: (value) => `${(value || 0).toFixed(2)}%`,
              },
            ]}
            yAxisLabel="Margin %"
            yAxisScaleType="linear"
            chartMargin={{ top: 70, right: 20, bottom: 90, left: 90 }}
            legendSlotProps={{
              direction: "row",
              position: { vertical: "top", horizontal: "middle" },
              padding: 0,
            }}
            height={320}
            onBarClick={(item) =>
              openDrillDown({
                title: `Margin Detail: ${item.serviceType}`,
                subtitle: `${(item.profitMargin || 0).toFixed(2)}% margin`,
                description: "Profitability detail for this service type.",
                data: [item],
                columns: [
                  { field: "serviceType", headerName: "Service Type", width: 180 },
                  { field: "servicesCount", headerName: "Services", width: 90 },
                  {
                    field: "revenue",
                    headerName: "Revenue",
                    width: 130,
                    renderCell: (row) => formatCurrency(row.revenue),
                  },
                  {
                    field: "profit",
                    headerName: "Profit",
                    width: 120,
                    renderCell: (row) => formatCurrency(row.profit),
                  },
                  {
                    field: "profitMargin",
                    headerName: "Margin %",
                    width: 100,
                    renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
                  },
                  {
                    field: "avgProfitPerService",
                    headerName: "Avg Profit/Service",
                    width: 140,
                    renderCell: (row) => formatCurrency(row.avgProfitPerService),
                  },
                ],
              })
            }
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ReportLineChart
            title="Monthly Revenue, Cost & Margin"
            tooltipTitle="Monthly Revenue, Cost & Margin"
            tooltipContent={`Tracks pricing health over time by plotting revenue and costs alongside margin percentage.

How to use:
• Revenue rising while margin falls = likely rate pressure.
• Revenue and margin both rising = healthy pricing power.
• Cost spikes with flat rates = execution leakage.

Tip:
Use month-level drill-down to identify exactly when margin inflected and which service types drove the shift.`}
            xAxisData={data.monthlyRows.map((row) => row.monthLabel)}
            series={[
              {
                label: "Revenue",
                data: data.monthlyRows.map((row) => row.revenue),
                color: chartColors.series[0],
                valueFormatter: (value) => formatCurrency(value),
                yAxisId: "moneyAxis",
              },
              {
                label: "Costs",
                data: data.monthlyRows.map((row) => row.costs),
                color: chartColors.series[4],
                valueFormatter: (value) => formatCurrency(value),
                yAxisId: "moneyAxis",
              },
              {
                label: "Margin %",
                data: data.monthlyRows.map((row) => row.margin),
                color: chartColors.series[2],
                valueFormatter: (value) => `${(value || 0).toFixed(2)}%`,
                yAxisId: "marginAxis",
              },
            ]}
            yAxisConfigs={[
              { id: "moneyAxis", label: "Revenue / Costs", min: 0 },
              { id: "marginAxis", label: "Margin %" },
            ]}
            leftAxis="moneyAxis"
            rightAxis="marginAxis"
            chartMargin={{ top: 20, right: 85, bottom: 80, left: 85 }}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: 0,
              },
            }}
            height={340}
            onMarkClick={(dataIndex) => {
              const item = data.monthlyRows[dataIndex];
              if (!item) return;
              openDrillDown({
                title: `Monthly Pricing Detail: ${item.monthLabel}`,
                subtitle: `${formatCurrency(item.revenue)} revenue · ${(item.margin || 0).toFixed(2)}% margin`,
                description: "Single-month pricing and margin snapshot.",
                data: [item],
                columns: [
                  { field: "monthLabel", headerName: "Month", width: 120 },
                  {
                    field: "revenue",
                    headerName: "Revenue",
                    width: 130,
                    renderCell: (row) => formatCurrency(row.revenue),
                  },
                  {
                    field: "costs",
                    headerName: "Costs",
                    width: 120,
                    renderCell: (row) => formatCurrency(row.costs),
                  },
                  {
                    field: "profit",
                    headerName: "Profit",
                    width: 120,
                    renderCell: (row) => formatCurrency(row.profit),
                  },
                  {
                    field: "margin",
                    headerName: "Margin %",
                    width: 100,
                    renderCell: (row) => `${(row.margin || 0).toFixed(2)}%`,
                  },
                  { field: "servicesCount", headerName: "Services", width: 90 },
                ],
              });
            }}
          />
        </Grid>
      </Grid>
    ),
    [
      chartColors.series,
      openDrillDown,
    ],
  );

  return (
    <>
      <DashboardReport
        title="Pricing & Rate Intelligence"
        description="Consolidates internal rate comparison and pricing optimization to improve competitiveness and margin quality."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={
          typeof drillDown.description === "string" ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
              {drillDown.description}
            </Typography>
          ) : (
            drillDown.description
          )
        }
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

const addMonthsToKey = (monthKey, count) => {
  if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) {
    const now = new Date();
    const dt = new Date(now.getFullYear(), now.getMonth() + count, 1);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
  }

  const [year, month] = monthKey.split("-").map(Number);
  const dt = new Date(year, month - 1 + count, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};

const linearForecast = (values = [], periods = 3) => {
  const clean = values.map((v) => Number(v || 0));
  if (clean.length === 0) return Array(periods).fill(0);
  if (clean.length === 1) return Array(periods).fill(clean[0]);

  const n = clean.length;
  const xMean = (n - 1) / 2;
  const yMean = clean.reduce((s, v) => s + v, 0) / n;

  let numerator = 0;
  let denominator = 0;
  for (let i = 0; i < n; i += 1) {
    numerator += (i - xMean) * (clean[i] - yMean);
    denominator += (i - xMean) ** 2;
  }

  const slope = denominator === 0 ? 0 : numerator / denominator;
  const intercept = yMean - slope * xMean;

  return Array.from({ length: periods }, (_, idx) => {
    const x = n + idx;
    return Math.max(0, intercept + slope * x);
  });
};

const getDemandChurnForecastData = async (filters) => {
  const [revenueSummaryRes, clientRetentionRes, clientActivityRes, serviceVolumeRes] =
    await Promise.all([
      api.get("/reports/financial/revenue-summary", { params: filters }),
      api.get("/reports/client/retention", { params: filters }),
      api.get("/reports/client/activity", { params: filters }),
      api.get("/reports/service/volume", { params: filters }),
    ]);

  const revenueData = revenueSummaryRes.data?.data || {};
  const retentionData = clientRetentionRes.data?.data || {};
  const activityRows = (clientActivityRes.data?.data?.tableData || []).map((row) => ({
    ...row,
    servicesCount: Number(row.servicesCount || 0),
    revenue: Number(row.revenue || 0),
    avgRevenuePerService: Number(row.avgRevenuePerService || 0),
    daysSinceLastService: Number(row.daysSinceLastService || 0),
  }));

  const volumeRows = (serviceVolumeRes.data?.data?.tableData || [])
    .map((row) => ({
      ...row,
      month: row.month,
      monthLabel: toMonthLabel(row.month),
      total: Number(row.total || 0),
      translation: Number(row.translation || 0),
      interpretation: Number(row.interpretation || 0),
      other: Number(row.other || 0),
    }))
    .sort((a, b) => (a.month || "").localeCompare(b.month || ""));

  const revenueByMonth = {};
  const monthlyTrend = revenueData.monthlyTrend || {};
  (monthlyTrend.months || []).forEach((month, index) => {
    revenueByMonth[month] = Number(monthlyTrend.revenue?.[index] || 0);
  });

  const demandRows = volumeRows.map((row) => ({
    ...row,
    revenue: Number(revenueByMonth[row.month] || 0),
  }));

  const forecastServices = linearForecast(
    demandRows.map((row) => row.total),
    3,
  );
  const forecastRevenue = linearForecast(
    demandRows.map((row) => row.revenue),
    3,
  );

  const lastMonthKey = demandRows[demandRows.length - 1]?.month;
  const forecastRows = forecastServices.map((services, index) => {
    const month = addMonthsToKey(lastMonthKey, index + 1);
    return {
      month,
      monthLabel: toMonthLabel(month),
      forecastServices: Number(services.toFixed(2)),
      forecastRevenue: Number((forecastRevenue[index] || 0).toFixed(2)),
    };
  });

  const atRiskClients = activityRows.filter((row) => row.daysSinceLastService >= 60);

  const topDormantClients = [...activityRows]
    .filter((row) => row.daysSinceLastService > 0)
    .sort((a, b) => b.daysSinceLastService - a.daysSinceLastService)
    .slice(0, 12);

  return {
    data: {
      summary: {
        totalServices: Number(revenueData.totalServices || 0),
        periodGrowth: Number(revenueData.periodGrowth || 0),
        clientRetentionRate: Number(
          (retentionData.retentionRate ?? revenueData.clientRetentionRate) || 0,
        ),
        clientChurnRate: Number(
          (retentionData.churnRate ?? revenueData.clientChurnRate) || 0,
        ),
        retainedClients: Number(retentionData.retainedClients || 0),
        newClients: Number(retentionData.newClients || 0),
        lostClients: Number(retentionData.lostClients || 0),
        currentPeriodClients: Number(retentionData.currentPeriodClients || 0),
        previousPeriodClients: Number(retentionData.previousPeriodClients || 0),
        next3MonthForecastServices: Number(
          forecastRows.reduce((sum, row) => sum + row.forecastServices, 0).toFixed(2),
        ),
        atRiskClients: atRiskClients.length,
      },
      demandRows,
      forecastRows,
      activityRows,
      atRiskClients,
      topDormantClients,
    },
  };
};

export const DemandChurnForecast = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [drillDown, setDrillDown] = useState(createDrawerInitialState());

  const openDrillDown = useCallback((config) => setDrillDown({ open: true, ...config }), []);
  const closeDrillDown = useCallback(
    () => setDrillDown((prev) => ({ ...prev, open: false })),
    [],
  );

  const dataFetchFn = useCallback((activeFilters) => getDemandChurnForecastData(activeFilters), []);

  const metricsConfig = useCallback(
    (data) => [
      {
        title: "Demand Growth",
        value: formatPercent(data.summary.periodGrowth || 0),
        subtitle: `${(data.summary.totalServices || 0).toLocaleString()} services in period`,
        color: data.summary.periodGrowth >= 0 ? "success.main" : "error.main",
        tooltipTitle: "Demand Growth",
        tooltipContent: `Demand Growth shows how quickly service demand is expanding or contracting for the selected period.

      Formula:
      Demand Growth % = (Current Period Demand - Prior Comparable Period Demand) / Prior Comparable Period Demand x 100

      How to read it:
      • Growth + rising churn can indicate unstable, short-lived demand.
      • Flat growth + rising churn usually signals future volume risk.
      • Growth + stable/improving retention is the healthiest pattern.

      Action:
      Validate growth quality by checking retention and inactivity risk together.`,
        onClick: () =>
          openDrillDown({
            title: "Demand History by Month",
            subtitle: "Actual demand and revenue",
            description: "Monthly demand profile by service category.",
            data: data.demandRows,
            columns: [
              { field: "monthLabel", headerName: "Month", width: 120 },
              { field: "total", headerName: "Total Services", width: 110 },
              { field: "translation", headerName: "Translation", width: 110 },
              { field: "interpretation", headerName: "Interpretation", width: 120 },
              { field: "other", headerName: "Other", width: 90 },
              {
                field: "revenue",
                headerName: "Revenue",
                width: 130,
                renderCell: (row) => formatCurrency(row.revenue),
              },
            ],
          }),
      },
      {
        title: "Client Churn Rate",
        value: `${(data.summary.clientChurnRate || 0).toFixed(2)}%`,
        subtitle: `${(data.summary.clientRetentionRate || 0).toFixed(2)}% retention`,
        color: data.summary.clientChurnRate <= 20 ? "success.main" : "warning.main",
        tooltipTitle: "Client Churn Rate",
        tooltipContent: `Client churn is the share of prior-period clients who did not return in the current period.

Formula:
      Churn % = Lost Clients / Previous Period Clients x 100

      How to read it:
      • Rising churn with rising demand often means acquisition is replacing lost clients.
      • Rising churn with flat demand is an early warning for revenue slowdown.

      Action:
      Pair churn with the dormant-client list to prioritize retention outreach.`,
        onClick: () =>
          openDrillDown({
            title: "Retention Snapshot",
            subtitle: "Current vs previous period client movement",
            description: "Use this to track retained, new and lost clients each cycle.",
            data: [
              {
                currentPeriodClients: data.summary.currentPeriodClients,
                previousPeriodClients: data.summary.previousPeriodClients,
                retainedClients: data.summary.retainedClients,
                newClients: data.summary.newClients,
                lostClients: data.summary.lostClients,
                retentionRate: data.summary.clientRetentionRate,
                churnRate: data.summary.clientChurnRate,
              },
            ],
            columns: [
              { field: "currentPeriodClients", headerName: "Current Clients", width: 120 },
              { field: "previousPeriodClients", headerName: "Previous Clients", width: 130 },
              { field: "retainedClients", headerName: "Retained", width: 100 },
              { field: "newClients", headerName: "New", width: 90 },
              { field: "lostClients", headerName: "Lost", width: 90 },
              {
                field: "retentionRate",
                headerName: "Retention %",
                width: 110,
                renderCell: (row) => `${(row.retentionRate || 0).toFixed(2)}%`,
              },
              {
                field: "churnRate",
                headerName: "Churn %",
                width: 100,
                renderCell: (row) => `${(row.churnRate || 0).toFixed(2)}%`,
              },
            ],
          }),
      },
      {
        title: "At-Risk Clients",
        value: `${data.summary.atRiskClients || 0}`,
        subtitle: "No service in last 60+ days",
        color: data.summary.atRiskClients <= 10 ? "success.main" : "warning.main",
        tooltipTitle: "At-Risk Clients",
        tooltipContent: `At-Risk Clients are accounts with no recent activity (60+ days inactive in this report).

      Why it matters:
      • Inactivity usually precedes churn.
      • High-value dormant clients can create sudden revenue gaps.

      Action:
      Start with high-revenue dormant clients for immediate reactivation campaigns.`,
        onClick: () =>
          openDrillDown({
            title: "At-Risk Client List",
            subtitle: `${data.atRiskClients.length} clients flagged`,
            description:
              "Prioritize this list for account check-ins, contract review, and proactive demand planning.",
            data: data.atRiskClients,
            columns: [
              { field: "clientName", headerName: "Client", width: 180 },
              { field: "clientType", headerName: "Type", width: 100 },
              { field: "servicesCount", headerName: "Services", width: 90 },
              {
                field: "revenue",
                headerName: "Revenue",
                width: 130,
                renderCell: (row) => formatCurrency(row.revenue),
              },
              {
                field: "avgRevenuePerService",
                headerName: "Avg Rev/Service",
                width: 140,
                renderCell: (row) => formatCurrency(row.avgRevenuePerService),
              },
              { field: "daysSinceLastService", headerName: "Days Inactive", width: 110 },
            ],
          }),
      },
      {
        title: "3-Month Demand Forecast",
        value: `${(data.summary.next3MonthForecastServices || 0).toLocaleString()} services`,
        subtitle: "Linear trend projection",
        tooltipTitle: "3-Month Demand Forecast",
        tooltipContent: `Forecast projects the next 3 months based on historical trend slope.

      Method:
      Simple linear trend projection from historical monthly demand and revenue.

      Use it to:
      • plan staffing and linguist availability,
      • set sales and fulfillment targets,
      • detect demand-capacity mismatches before they hit SLA.

      Caution:
      Treat as baseline guidance, then scenario-test for seasonality or one-off events.`,
        onClick: () =>
          openDrillDown({
            title: "Forecast Detail",
            subtitle: "Projected monthly demand and revenue",
            description:
              "Simple linear forecast from historical monthly demand and revenue trend.",
            data: data.forecastRows,
            columns: [
              { field: "monthLabel", headerName: "Forecast Month", width: 130 },
              { field: "forecastServices", headerName: "Forecast Services", width: 120 },
              {
                field: "forecastRevenue",
                headerName: "Forecast Revenue",
                width: 130,
                renderCell: (row) => formatCurrency(row.forecastRevenue),
              },
            ],
          }),
      },
    ],
    [openDrillDown],
  );

  const chartsConfig = useCallback(
    (data) => {
      const labels = [
        ...data.demandRows.map((row) => row.monthLabel),
        ...data.forecastRows.map((row) => row.monthLabel),
      ];

      const actualSeries = [
        ...data.demandRows.map((row) => row.total),
        ...data.forecastRows.map(() => null),
      ];

      const forecastSeries = [
        ...data.demandRows.map(() => null),
        ...data.forecastRows.map((row) => row.forecastServices),
      ];

      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <ReportLineChart
              title="Demand Trend & 3-Month Forecast"
              tooltipTitle="Demand Trend & Forecast"
              tooltipContent={`Shows historical monthly demand and projected future demand.

Read it this way:
• Sharp divergence between trend and forecast requires scenario planning.
• Forecast drop with rising churn indicates near-term utilization risk.
• Forecast rise with flat capacity indicates staffing pressure risk.

Action:
Use this chart to align sales commitments with operational capacity each month.`}
              xAxisData={labels}
              series={[
                {
                  label: "Actual Services",
                  data: actualSeries,
                  color: chartColors.series[0],
                  yAxisId: "servicesAxis",
                  valueFormatter: (value) => `${(value || 0).toLocaleString()} services`,
                },
                {
                  label: "Forecast Services",
                  data: forecastSeries,
                  color: chartColors.series[2],
                  yAxisId: "servicesAxis",
                  curve: "monotoneX",
                  valueFormatter: (value) => `${(value || 0).toLocaleString()} services`,
                },
              ]}
              yAxisConfigs={[{ id: "servicesAxis", label: "Services", min: 0 }]}
              leftAxis="servicesAxis"
              chartMargin={{ top: 20, right: 80, bottom: 80, left: 80 }}
              slotProps={{
                legend: {
                  direction: "row",
                  position: { vertical: "bottom", horizontal: "middle" },
                  padding: 0,
                },
              }}
              height={340}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Monthly Demand by Service Category"
              tooltipTitle="Monthly Demand by Service Category"
              tooltipContent={`Breaks monthly demand into Translation, Interpretation, and Other.

Use this to spot mix shifts that affect staffing and profitability.

What to watch:
• Rising Interpretation share can increase scheduling complexity.
• Rising Translation share may require different linguist allocation.

Action:
Adjust capacity plan by category, not just total demand.`}
              data={data.demandRows}
              xAxisKey="monthLabel"
              series={[
                {
                  dataKey: "translation",
                  label: "Translation",
                  color: chartColors.series[0],
                  valueFormatter: (value) => `${(value || 0).toLocaleString()} services`,
                },
                {
                  dataKey: "interpretation",
                  label: "Interpretation",
                  color: chartColors.series[1],
                  valueFormatter: (value) => `${(value || 0).toLocaleString()} services`,
                },
                {
                  dataKey: "other",
                  label: "Other",
                  color: chartColors.series[3],
                  valueFormatter: (value) => `${(value || 0).toLocaleString()} services`,
                },
              ]}
              yAxisLabel="Services"
              yAxisScaleType="linear"
              chartMargin={{ top: 20, right: 20, bottom: 85, left: 75 }}
              legendSlotProps={{
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: 0,
              }}
              height={320}
              onBarClick={(item) =>
                openDrillDown({
                  title: `Demand Mix Detail: ${item.monthLabel}`,
                  subtitle: `${item.total} total services`,
                  description: "Service-category mix and revenue for selected month.",
                  data: [item],
                  columns: [
                    { field: "monthLabel", headerName: "Month", width: 120 },
                    { field: "total", headerName: "Total", width: 90 },
                    { field: "translation", headerName: "Translation", width: 110 },
                    { field: "interpretation", headerName: "Interpretation", width: 120 },
                    { field: "other", headerName: "Other", width: 90 },
                    {
                      field: "revenue",
                      headerName: "Revenue",
                      width: 130,
                      renderCell: (row) => formatCurrency(row.revenue),
                    },
                  ],
                })
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Top Dormant Clients (Churn Risk)"
              tooltipTitle="Top Dormant Clients"
              tooltipContent={`Clients ranked by days since last service.

Higher inactivity often correlates with churn risk. Combine with revenue to prioritize outreach.

Prioritization rule:
Target clients with both high inactivity days and high historical revenue first.

Action:
Build a weekly reactivation list directly from this chart drill-down.`}
              data={data.topDormantClients}
              xAxisKey="clientName"
              series={[
                {
                  dataKey: "daysSinceLastService",
                  label: "Days Inactive",
                  color: chartColors.series[4],
                  valueFormatter: (value) => `${(value || 0).toFixed(0)} days`,
                },
              ]}
              yAxisLabel="Days"
              yAxisScaleType="linear"
              chartMargin={{ top: 20, right: 20, bottom: 85, left: 75 }}
              legendSlotProps={{
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: 0,
              }}
              height={320}
              onBarClick={(item) =>
                openDrillDown({
                  title: `Dormancy Detail: ${item.clientName}`,
                  subtitle: `${item.daysSinceLastService} days inactive`,
                  description: "Client-level activity, revenue and inactivity risk profile.",
                  data: [item],
                  columns: [
                    { field: "clientName", headerName: "Client", width: 180 },
                    { field: "clientType", headerName: "Type", width: 100 },
                    { field: "servicesCount", headerName: "Services", width: 90 },
                    {
                      field: "revenue",
                      headerName: "Revenue",
                      width: 130,
                      renderCell: (row) => formatCurrency(row.revenue),
                    },
                    {
                      field: "avgRevenuePerService",
                      headerName: "Avg Rev/Service",
                      width: 140,
                      renderCell: (row) => formatCurrency(row.avgRevenuePerService),
                    },
                    { field: "daysSinceLastService", headerName: "Days Inactive", width: 110 },
                  ],
                })
              }
            />
          </Grid>
        </Grid>
      );
    },
    [chartColors.series, openDrillDown],
  );

  return (
    <>
      <DashboardReport
        title="Demand, Churn & Forecast"
        description="Combines demand forecasting with churn-risk analytics to support capacity planning and retention strategy."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={
          typeof drillDown.description === "string" ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
              {drillDown.description}
            </Typography>
          ) : (
            drillDown.description
          )
        }
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

const getMarketGrowthOpportunitiesData = async (filters) => {
  const [revenueByClientRes, revenueSummaryRes, clientActivityRes, revenueByServiceRes] =
    await Promise.all([
      api.get("/reports/financial/revenue-by-client", { params: filters }),
      api.get("/reports/financial/revenue-summary", { params: filters }),
      api.get("/reports/client/activity", { params: filters }),
      api.get("/reports/financial/revenue-by-service", { params: filters }),
    ]);

  const clientData = revenueByClientRes.data?.data || {};
  const summaryData = revenueSummaryRes.data?.data || {};
  const activityRows = (clientActivityRes.data?.data?.tableData || []).map((row) => ({
    ...row,
    servicesCount: Number(row.servicesCount || 0),
    revenue: Number(row.revenue || 0),
    avgRevenuePerService: Number(row.avgRevenuePerService || 0),
    daysSinceLastService: Number(row.daysSinceLastService || 0),
  }));
  const serviceRows = (revenueByServiceRes.data?.data?.tableData || []).map((row) => ({
    ...row,
    revenue: Number(row.revenue || 0),
    cost: Number(row.cost || 0),
    profit: Number(row.profit || 0),
    servicesCount: Number(row.servicesCount || 0),
    profitMargin: Number(row.profitMargin || 0),
    avgRevenuePerService: Number(row.avgRevenuePerService || 0),
  }));

  const totalRevenue = Number(clientData.totalRevenue || summaryData.totalRevenue || 0);
  const newClientRevenue = Number(clientData.newClientRevenue || 0);

  const maxServices = Math.max(...serviceRows.map((row) => row.servicesCount), 1);
  const opportunityRows = [...serviceRows]
    .map((row) => {
      const marginScore = Math.max(0, Math.min(100, row.profitMargin));
      const scarcityScore = ((maxServices - row.servicesCount) / maxServices) * 100;
      const opportunityScore = (marginScore * 0.6) + (scarcityScore * 0.4);
      return {
        ...row,
        opportunityScore: Number(opportunityScore.toFixed(2)),
      };
    })
    .sort((a, b) => b.opportunityScore - a.opportunityScore)
    .slice(0, 12);

  const dormantHighValueClients = [...activityRows]
    .filter((row) => row.daysSinceLastService >= 45)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 12);

  const tierRows = [
    {
      tier: "Top Tier",
      clients: Number(clientData.topTier?.count || 0),
      revenue: Number(clientData.topTier?.revenue || 0),
      percent: Number(clientData.topTier?.percentage || 0),
    },
    {
      tier: "Middle Tier",
      clients: Number(clientData.midTier?.count || 0),
      revenue: Number(clientData.midTier?.revenue || 0),
      percent: Number(clientData.midTier?.percentage || 0),
    },
    {
      tier: "Long Tail",
      clients: Number(clientData.bottomTier?.count || 0),
      revenue: Number(clientData.bottomTier?.revenue || 0),
      percent: Number(clientData.bottomTier?.percentage || 0),
    },
  ];

  const topClientsRows = (clientData.tableData || []).slice(0, 12).map((row) => ({
    ...row,
    revenue: Number(row.revenue || 0),
    profitMargin: Number(row.profitMargin || 0),
    servicesCount: Number(row.servicesCount || 0),
    yoyGrowth: Number(row.yoyGrowth || 0),
  }));

  return {
    data: {
      summary: {
        concentrationRatio: Number(clientData.concentrationRatio || summaryData.concentrationRatio || 0),
        top20Count: Number(clientData.top20Count || 0),
        top20Revenue: Number(clientData.top20Revenue || 0),
        customerLifetimeValue: Number(summaryData.customerLifetimeValue || 0),
        newClientRevenueShare: totalRevenue > 0 ? (newClientRevenue / totalRevenue) * 100 : 0,
        totalClients: Number(clientData.totalClients || 0),
        opportunityIndex:
          opportunityRows.length > 0
            ? Number(
                (
                  opportunityRows.reduce((sum, row) => sum + row.opportunityScore, 0) /
                  opportunityRows.length
                ).toFixed(2),
              )
            : 0,
      },
      topClientsRows,
      tierRows,
      opportunityRows,
      dormantHighValueClients,
      clientTypeBreakdown: (clientData.clientTypeBreakdown || []).map((row) => ({
        ...row,
        count: Number(row.count || 0),
        revenue: Number(row.revenue || 0),
        percentage: Number(row.percentage || 0),
      })),
    },
  };
};

export const MarketGrowthOpportunities = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [drillDown, setDrillDown] = useState(createDrawerInitialState());

  const openDrillDown = useCallback((config) => setDrillDown({ open: true, ...config }), []);
  const closeDrillDown = useCallback(
    () => setDrillDown((prev) => ({ ...prev, open: false })),
    [],
  );

  const dataFetchFn = useCallback((activeFilters) => getMarketGrowthOpportunitiesData(activeFilters), []);

  const metricsConfig = useCallback(
    (data) => [
      {
        title: "Revenue Concentration",
        value: `${(data.summary.concentrationRatio || 0).toFixed(2)}%`,
        subtitle: `Top ${data.summary.top20Count || 0} clients share`,
        color: data.summary.concentrationRatio <= 55 ? "success.main" : "warning.main",
        tooltipTitle: "Revenue Concentration",
        tooltipContent: `Revenue Concentration shows how much revenue is dependent on top clients.

Formula:
Concentration % = Revenue from Top 20% Clients / Total Revenue x 100

How to read it:
• High concentration means higher single-account risk.
• Moderate concentration can still be healthy if top accounts are growing and diversified by sector.

Action:
Protect top accounts with retention plans while growing mid-tier accounts to reduce concentration risk.`,
        onClick: () =>
          openDrillDown({
            title: "Top Client Concentration",
            subtitle: `${(data.summary.concentrationRatio || 0).toFixed(2)}% concentration`,
            description: "Revenue and margin profile of the highest-contributing clients.",
            data: data.topClientsRows,
            columns: [
              { field: "clientName", headerName: "Client", width: 180 },
              {
                field: "revenue",
                headerName: "Revenue",
                width: 130,
                renderCell: (row) => formatCurrency(row.revenue),
              },
              { field: "servicesCount", headerName: "Services", width: 90 },
              {
                field: "profitMargin",
                headerName: "Margin %",
                width: 100,
                renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
              },
              {
                field: "yoyGrowth",
                headerName: "YoY %",
                width: 90,
                renderCell: (row) => `${(row.yoyGrowth || 0).toFixed(2)}%`,
              },
            ],
          }),
      },
      {
        title: "New Client Revenue Share",
        value: `${(data.summary.newClientRevenueShare || 0).toFixed(2)}%`,
        subtitle: "Portion from newly acquired clients",
        color: data.summary.newClientRevenueShare >= 20 ? "success.main" : "warning.main",
        tooltipTitle: "New Client Revenue Share",
        tooltipContent: `New Client Revenue Share measures how much revenue is generated by newly acquired clients.

      Formula:
      New Client Share % = Revenue from New Clients / Total Revenue x 100

      How to read it:
      • Very low share can indicate weak acquisition.
      • Very high share with weak retention may indicate unstable growth.

      Action:
      Pair this metric with churn and retention to separate healthy acquisition from replace-only growth.`,
        onClick: () =>
          openDrillDown({
            title: "Client Segment Contribution",
            subtitle: "Revenue split by client tiers",
            description: "Top-tier, mid-tier and long-tail contribution to total revenue.",
            data: data.tierRows,
            columns: [
              { field: "tier", headerName: "Tier", width: 130 },
              { field: "clients", headerName: "# Clients", width: 90 },
              {
                field: "revenue",
                headerName: "Revenue",
                width: 130,
                renderCell: (row) => formatCurrency(row.revenue),
              },
              {
                field: "percent",
                headerName: "Share %",
                width: 100,
                renderCell: (row) => `${(row.percent || 0).toFixed(2)}%`,
              },
            ],
          }),
      },
      {
        title: "Customer Lifetime Value",
        value: formatCurrency(data.summary.customerLifetimeValue || 0),
        subtitle: `${(data.summary.totalClients || 0).toLocaleString()} active clients`,
        tooltipTitle: "Customer Lifetime Value (CLV)",
        tooltipContent: `CLV here represents average lifetime revenue per active client in your selected context.

      How to read it:
      • Rising CLV with stable churn suggests stronger account depth.
      • Flat CLV with rising churn often indicates fragile account expansion.

      Action:
      Use CLV with client-type mix to identify which segments produce durable long-term value.`,
        onClick: () =>
          openDrillDown({
            title: "Client Type Breakdown",
            subtitle: "Revenue and mix by client type",
            description: "Use this to identify which client segments are expanding fastest.",
            data: data.clientTypeBreakdown,
            columns: [
              { field: "type", headerName: "Client Type", width: 150 },
              { field: "count", headerName: "# Clients", width: 90 },
              {
                field: "revenue",
                headerName: "Revenue",
                width: 130,
                renderCell: (row) => formatCurrency(row.revenue),
              },
              {
                field: "percentage",
                headerName: "Share %",
                width: 100,
                renderCell: (row) => `${(row.percentage || 0).toFixed(2)}%`,
              },
            ],
          }),
      },
      {
        title: "Opportunity Index",
        value: `${(data.summary.opportunityIndex || 0).toFixed(2)}`,
        subtitle: "High-margin, low-penetration potential",
        color: data.summary.opportunityIndex >= 45 ? "success.main" : "warning.main",
        tooltipTitle: "Opportunity Index",
        tooltipContent: `Opportunity Index is a composite expansion score for service types.

      Method in this report:
      Score = (Margin Score x 0.6) + (Low-Penetration Score x 0.4)

      How to read it:
      • Higher score = attractive economics plus room to grow.
      • Lower score = either weak margins or already saturated demand.

      Action:
      Prioritize high-score services for cross-sell campaigns and targeted sales enablement.`,
        onClick: () =>
          openDrillDown({
            title: "Top Service Expansion Opportunities",
            subtitle: "Ranked by opportunity score",
            description: "Prioritize these service types for targeted commercial and cross-sell campaigns.",
            data: data.opportunityRows,
            columns: [
              { field: "serviceType", headerName: "Service Type", width: 180 },
              { field: "category", headerName: "Category", width: 110 },
              { field: "servicesCount", headerName: "Services", width: 90 },
              {
                field: "revenue",
                headerName: "Revenue",
                width: 130,
                renderCell: (row) => formatCurrency(row.revenue),
              },
              {
                field: "profitMargin",
                headerName: "Margin %",
                width: 100,
                renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
              },
              { field: "opportunityScore", headerName: "Opp. Score", width: 100 },
            ],
          }),
      },
    ],
    [openDrillDown],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Top Clients by Revenue"
            tooltipTitle="Top Clients by Revenue"
            tooltipContent={`Ranks clients by revenue contribution.

Use this to balance account concentration risk with expansion focus.

What to watch:
• High revenue + weak margin = repricing opportunity.
• High revenue + negative YoY trend = retention risk.

Action:
Create account plans for top contributors before pursuing net-new growth.`}
            data={data.topClientsRows.slice(0, 10)}
            xAxisKey="clientName"
            series={[
              {
                dataKey: "revenue",
                label: "Revenue",
                color: chartColors.series[0],
                valueFormatter: (value) => formatCurrency(value),
              },
            ]}
            yAxisLabel="Revenue"
            yAxisScaleType="linear"
            chartMargin={{ top: 20, right: 20, bottom: 85, left: 85 }}
            legendSlotProps={{
              direction: "row",
              position: { vertical: "bottom", horizontal: "middle" },
              padding: 0,
            }}
            height={320}
            onBarClick={(item) =>
              openDrillDown({
                title: `Client Detail: ${item.clientName}`,
                subtitle: `${formatCurrency(item.revenue)} revenue`,
                description: "Client contribution and growth profile.",
                data: [item],
                columns: [
                  { field: "clientName", headerName: "Client", width: 180 },
                  {
                    field: "revenue",
                    headerName: "Revenue",
                    width: 130,
                    renderCell: (row) => formatCurrency(row.revenue),
                  },
                  { field: "servicesCount", headerName: "Services", width: 90 },
                  {
                    field: "profitMargin",
                    headerName: "Margin %",
                    width: 100,
                    renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
                  },
                  {
                    field: "yoyGrowth",
                    headerName: "YoY %",
                    width: 90,
                    renderCell: (row) => `${(row.yoyGrowth || 0).toFixed(2)}%`,
                  },
                ],
              })
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Revenue Share by Client Tier"
            tooltipTitle="Revenue Share by Client Tier"
            tooltipContent={`Shows revenue distribution across top-tier, middle-tier, and long-tail clients.

Healthy growth usually combines strong top-tier depth with a broadening long tail.

Interpretation:
• Overweight top tier may increase concentration risk.
• Underdeveloped mid tier often signals expansion whitespace.

Action:
Set growth targets by tier instead of only by total revenue.`}
            data={data.tierRows}
            xAxisKey="tier"
            series={[
              {
                dataKey: "percent",
                label: "Revenue Share %",
                color: chartColors.series[1],
                valueFormatter: (value) => `${(value || 0).toFixed(2)}%`,
              },
            ]}
            yAxisLabel="Share %"
            yAxisScaleType="linear"
            chartMargin={{ top: 20, right: 20, bottom: 85, left: 75 }}
            legendSlotProps={{
              direction: "row",
              position: { vertical: "bottom", horizontal: "middle" },
              padding: 0,
            }}
            height={320}
            onBarClick={(item) =>
              openDrillDown({
                title: `Tier Detail: ${item.tier}`,
                subtitle: `${(item.percent || 0).toFixed(2)}% revenue share`,
                description: "Tier-level contribution summary.",
                data: [item],
                columns: [
                  { field: "tier", headerName: "Tier", width: 130 },
                  { field: "clients", headerName: "# Clients", width: 90 },
                  {
                    field: "revenue",
                    headerName: "Revenue",
                    width: 130,
                    renderCell: (row) => formatCurrency(row.revenue),
                  },
                  {
                    field: "percent",
                    headerName: "Share %",
                    width: 100,
                    renderCell: (row) => `${(row.percent || 0).toFixed(2)}%`,
                  },
                ],
              })
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Service Expansion Opportunities"
            tooltipTitle="Service Expansion Opportunities"
            tooltipContent={`Opportunity score ranks service types with high margin and lower current penetration.

Use this to prioritize cross-sell and market expansion bets.

What to watch:
• High score + low volume = near-term cross-sell candidate.
• High score + strong existing volume = scale candidate.

Action:
Build service-specific go-to-market plays for the top-ranked opportunities.`}
            data={data.opportunityRows}
            xAxisKey="serviceType"
            series={[
              {
                dataKey: "opportunityScore",
                label: "Opportunity Score",
                color: chartColors.series[2],
                valueFormatter: (value) => `${(value || 0).toFixed(2)}`,
              },
            ]}
            yAxisLabel="Score"
            yAxisScaleType="linear"
            chartMargin={{ top: 20, right: 20, bottom: 85, left: 75 }}
            legendSlotProps={{
              direction: "row",
              position: { vertical: "bottom", horizontal: "middle" },
              padding: 0,
            }}
            height={320}
            onBarClick={(item) =>
              openDrillDown({
                title: `Opportunity Detail: ${item.serviceType}`,
                subtitle: `Score ${item.opportunityScore}`,
                description: "Revenue, margin and volume profile for this service type.",
                data: [item],
                columns: [
                  { field: "serviceType", headerName: "Service Type", width: 180 },
                  { field: "category", headerName: "Category", width: 110 },
                  { field: "servicesCount", headerName: "Services", width: 90 },
                  {
                    field: "revenue",
                    headerName: "Revenue",
                    width: 130,
                    renderCell: (row) => formatCurrency(row.revenue),
                  },
                  {
                    field: "profitMargin",
                    headerName: "Margin %",
                    width: 100,
                    renderCell: (row) => `${(row.profitMargin || 0).toFixed(2)}%`,
                  },
                  { field: "opportunityScore", headerName: "Opp. Score", width: 100 },
                ],
              })
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Dormant High-Value Clients"
            tooltipTitle="Dormant High-Value Clients"
            tooltipContent={`Ranks inactive clients by revenue contribution.

These are immediate reactivation opportunities with outsized growth impact.

Prioritization rule:
Start with clients that combine high historical revenue and long inactivity windows.

Action:
Run targeted reactivation outreach with service recommendations based on prior mix.`}
            data={data.dormantHighValueClients}
            xAxisKey="clientName"
            series={[
              {
                dataKey: "daysSinceLastService",
                label: "Days Inactive",
                color: chartColors.series[4],
                valueFormatter: (value) => `${(value || 0).toFixed(0)} days`,
              },
            ]}
            yAxisLabel="Days"
            yAxisScaleType="linear"
            chartMargin={{ top: 20, right: 20, bottom: 85, left: 75 }}
            legendSlotProps={{
              direction: "row",
              position: { vertical: "bottom", horizontal: "middle" },
              padding: 0,
            }}
            height={320}
            onBarClick={(item) =>
              openDrillDown({
                title: `Dormant Client Detail: ${item.clientName}`,
                subtitle: `${item.daysSinceLastService} days inactive`,
                description: "Client-level dormancy and revenue profile for reactivation planning.",
                data: [item],
                columns: [
                  { field: "clientName", headerName: "Client", width: 180 },
                  { field: "clientType", headerName: "Type", width: 100 },
                  { field: "servicesCount", headerName: "Services", width: 90 },
                  {
                    field: "revenue",
                    headerName: "Revenue",
                    width: 130,
                    renderCell: (row) => formatCurrency(row.revenue),
                  },
                  {
                    field: "avgRevenuePerService",
                    headerName: "Avg Rev/Service",
                    width: 140,
                    renderCell: (row) => formatCurrency(row.avgRevenuePerService),
                  },
                  { field: "daysSinceLastService", headerName: "Days Inactive", width: 110 },
                ],
              })
            }
          />
        </Grid>
      </Grid>
    ),
    [chartColors.series, openDrillDown],
  );

  return (
    <>
      <DashboardReport
        title="Market & Growth Opportunities"
        description="Consolidates cross-sell, market-trend, and opportunity scouting insights to prioritize strategic expansion bets."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={
          typeof drillDown.description === "string" ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
              {drillDown.description}
            </Typography>
          ) : (
            drillDown.description
          )
        }
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

export const ADVANCED_ANALYTICS_REPORTS = {
  "business-trends": GrowthBusinessTrends,
  "service-mix": ServiceMixEfficiency,
  "price-optimization": PricingRateIntelligence,
  "demand-forecast": DemandChurnForecast,
  "growth-opportunities": MarketGrowthOpportunities,
};

